import PrintableTableContentRow from "../../../../components/utils/printable-table-generator/PrintableTableContentRow";
import PrintableTableFooterRow from "../../../../components/utils/printable-table-generator/PrintableTableFooterRow";
import PrintableTableHeaderRow from "../../../../components/utils/printable-table-generator/PrintableTableHeaderRow";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";

// builds a compact page list with ellipses, e.g. [1, 2, 3, 4, "...", 20]
const getPageItems = (currentPage, totalPages, windowSize = 4) => {
  if (totalPages <= 1) return [1];

  let start = Math.max(1, currentPage - Math.floor((windowSize - 1) / 2));
  let end = Math.min(start + windowSize - 1, totalPages);
  if (end - start + 1 < windowSize) {
    start = Math.max(1, end - windowSize + 1);
  }

  const items = [];

  if (start > 1) {
    items.push(1);
    if (start > 2) items.push("...");
  }

  for (let page = start; page <= end; page++) items.push(page);

  if (end < totalPages) {
    if (end < totalPages - 1) items.push("...");
    items.push(totalPages);
  }

  return items;
};

const SimplePaginator = ({ first, rows, totalRecords, rowsPerPageOptions, onPageChange }) => {
  const totalPages = Math.max(1, Math.ceil(totalRecords / rows));
  const currentPage = Math.floor(first / rows) + 1;
  const pageItems = getPageItems(currentPage, totalPages);

  const goToPage = (page) => {
    const clamped = Math.min(Math.max(page, 1), totalPages);
    onPageChange({ first: (clamped - 1) * rows, rows });
  };

  return (
    <div className="row-center-center gap-1">
      <Button
        type="button"
        icon="pi pi-angle-double-left"
        className="p-button-text p-button-rounded"
        disabled={currentPage === 1}
        onClick={() => goToPage(1)}
      />
      <Button
        type="button"
        icon="pi pi-angle-left"
        className="p-button-text p-button-rounded"
        disabled={currentPage === 1}
        onClick={() => goToPage(currentPage - 1)}
      />

      <div className="row-center-center gap-1">
        {pageItems.map((item, idx) =>
          item === "..." ? (
            <span key={`ellipsis-${idx}`} className="px-1 text-slate-400">
              ...
            </span>
          ) : (
            <Button
              type="button"
              key={item}
              label={String(item)}
              className="p-button-text p-button-rounded"
              style={
                item === currentPage
                  ? { backgroundColor: "#eef2ff", color: "#4338ca" }
                  : undefined
              }
              onClick={() => goToPage(item)}
            />
          )
        )}
      </div>

      <Button
        type="button"
        icon="pi pi-angle-right"
        className="p-button-text p-button-rounded"
        disabled={currentPage === totalPages}
        onClick={() => goToPage(currentPage + 1)}
      />
      <Button
        type="button"
        icon="pi pi-angle-double-right"
        className="p-button-text p-button-rounded"
        disabled={currentPage === totalPages}
        onClick={() => goToPage(totalPages)}
      />

      <Dropdown
        className="ml-2"
        value={rows}
        options={rowsPerPageOptions.map((opt) => ({ label: `${opt}`, value: opt }))}
        onChange={(e) => onPageChange({ first: 0, rows: e.value })}
      />
    </div>
  );
};

const settings = [
  { label: "NO", field: "no", width: "w-[3rem]", layout: "row-center-start" },
  { label: "PRODUCT", field: "product_name", width: "w-[23.5rem]", layout: "row-start-start", wrap: true },
  { label: "QTY BUY", field: "total_quantity_buy", width: "w-[7.5rem]", layout: "row-end-start", type: "number" },
  { label: "QTY FREE", field: "total_quantity_free", width: "w-[7.5rem]", layout: "row-end-start", type: "number" },
  { label: "QTY PAKAI", field: "total_quantity_pemakaian", width: "w-[7.5rem]", layout: "row-end-start", type: "number" },
  { label: "QTY SISA", field: "total_quantity_sisa", width: "w-[7.5rem]", layout: "row-end-start", type: "number" },
  { label: "CUST BUY", field: "total_customer_buy", width: "w-[7.5rem]", layout: "row-end-start", type: "number" },
  { label: "CUST REBUY", field: "total_customer_rebuy", width: "w-[8rem]", layout: "row-end-start", type: "number" },
  { label: "REBUY %", field: "total_customer_rebuy_percentage", width: "w-[8rem]", layout: "row-end-start", type: "percent" },
];

const footerSettings = [
  { field: "label", width: "w-[25rem]", layout: "row-end-start", colSpan: 2 },
  { label: "", field: "_blank", width: "w-[8rem]", layout: "row-start-start", type: "*" },
];

const ReportTemplateGroup = (props) => {
  const { list, title, pagination, totals, onProductClick } = props;

  const rows = list.map((item, idx) => ({ ...item, no: item.no ?? idx + 1 }));

  const contentSettings = onProductClick
    ? settings.map((setting) =>
        setting.field === "product_name" ? { ...setting, onClick: onProductClick } : setting
      )
    : settings;

  const totalCustomerBuy =
    totals?.total_customer_buy ?? rows.reduce((sum, row) => sum + Number(row?.total_customer_buy || 0), 0);
  const totalQuantity =
    totals?.total_quantity ?? rows.reduce((sum, row) => sum + Number(row?.total_quantity || 0), 0);
  const totalLabel = totals?.label ?? "TOTAL";

  return (
    <>
      <table>
        {/* locks each column to a fixed width shared by every row, so the
            footer's colSpan cell can't throw off border alignment against
            the header/content rows' individually-widthed cells */}
        <colgroup>
          {settings.map((setting, idx) => (
            <col key={idx} className={setting.width} />
          ))}
        </colgroup>

        <PrintableTableHeaderRow settings={settings} title={title} />

        <tbody>
          {rows.map((row, idx) => (
            <PrintableTableContentRow
              key={row.product_id ? `${row.product_id}-${idx}` : idx}
              settings={contentSettings}
              data={row}
            />
          ))}
        </tbody>

        {/* <PrintableTableFooterRow
          settings={footerSettings}
          data={{ label: totalLabel, total_customer_buy: totalCustomerBuy, total_quantity: totalQuantity }}
        /> */}
      </table>

      {pagination && (
        <div className="print:hidden row-center-center w-full pt-2">
          <SimplePaginator
            first={pagination.first}
            rows={pagination.rows}
            totalRecords={pagination.totalRecords}
            rowsPerPageOptions={pagination.rowsPerPageOptions}
            onPageChange={pagination.onPageChange}
          />
        </div>
      )}
    </>
  );
};

export default ReportTemplateGroup;
